import React, { useContext, useState } from 'react';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { CurrencyValue } from '@jutro/components';

import { MLProductCodeUtil, MLLocalDateUtil } from 'ml-portals-util-js';
import _ from 'lodash';

import messages from './MLSelectPaymentAmountComponent.messages.js';
import metadata from './MLSelectPaymentAmountComponent.metadata.json5';
import styles from './MLSelectPaymentAmountComponent.module.scss';

import { CurrencyField } from '@jutro/legacy/components';

import { IntlContext } from '@jutro/legacy/locale';

const AMOUNT_OPTIONS = {
    CURRENT_DUE: 'current-due',
    DUE_TO_REINSTATE: 'due-to-reinstate',
    STANDARD_INSTALLMENT: 'standard-installment',
    ENTIRE_BALANCE: 'entire-balance',
    OTHER_AMOUNT: 'other-amount'
};

// MLSelectPaymentAmount.js directive
function MLSelectPaymentAmountComponent(props) {
    const translator = useTranslator();
    const intl = useContext(IntlContext);

    const {
        policiesBillingData,
        paymentRequests,
        selectedAmountOption,
        isGpa,
        onUpdatePaymentAmount
    } = props;

    const isAutoPolicy = (productCode) => MLProductCodeUtil.isPersonalAuto(productCode);
    const isHomePolicy = (productCode) => MLProductCodeUtil.isHomeOwners(productCode);
    const isUmbrellaPolicy = (productCode) => MLProductCodeUtil.isPersonalUmbrella(productCode);

    const [totalOtherAmount] = useState({ amount: 0, currency: 'usd' });
    // const [selectedPolicy, setSelectedPolicy] = useState(undefined);
    let showNonPayStatusMessage = false;

    const totalAmountDue = { amount: 0, currency: 'usd' };
    const totalAmountDueToReinstate = { amount: 0, currency: 'usd' };
    const totalInstallment = { amount: 0, currency: 'usd' };
    const totalBalance = { amount: 0, currency: 'usd' };
    const totalPastDue = { amount: 0, currency: 'usd' };

    const isCancelledWithin7Days = (policyDetails) => {
        return policyDetails.isCancelled ?
            Math.abs(MLLocalDateUtil.differenceInDays(MLLocalDateUtil.localDate2Date(policyDetails.cancelledDate_ML), new Date())) <= 7 : false;
    };

    const isNotReinstateState = (policy) => {
        const statesToPayInFull = ['WV'];
        const { state } = policy.policyDetails.address_ML;

        return statesToPayInFull.includes(state);
    };

    const isReinstateOptionAvailable = (policies) => {
        return policies && policies.some((policy) => policy.selected && policy.policyDetails.isCancelled && !isNotReinstateState(policy) && policy.reinstatementAmountDue_ML.amount > 0
            && isCancelledWithin7Days(policy.policyDetails));
    };

    const getEarlierTermRemainingAmount = (policy) => {
        if (!policy.previousTermsRemainingAmounts) {
            return 0;
        }
        return policy.previousTermsRemainingAmounts;
    };

    const getEarlierTermOutstandingAmount = (policy) => {
        if (!policy.previousTermsOutstandingAmounts) {
            return 0;
        }
        return policy.previousTermsOutstandingAmounts;
    };

    const setAmountToPay = (paymentRequest, selectedOption) => {
        const policy = policiesBillingData.find((p) => p.policyNumber_ML === paymentRequest.policyNumber && p.selected);

        switch (selectedOption) {
            case AMOUNT_OPTIONS.CURRENT_DUE:
                paymentRequest.paymentAmount = (policy.totalOutstandingAmount_ML.amount + getEarlierTermOutstandingAmount(policy)).toFixed(2);
                return;
            case AMOUNT_OPTIONS.DUE_TO_REINSTATE:
                paymentRequest.paymentAmount = policy.reinstatementAmountDue_ML.amount;
                return;
            case AMOUNT_OPTIONS.STANDARD_INSTALLMENT:
                paymentRequest.paymentAmount = policy.installmentAmount_ML.amount;
                return;
            case AMOUNT_OPTIONS.ENTIRE_BALANCE:
                paymentRequest.paymentAmount = (policy.remainingPolicyBalance_ML.amount + getEarlierTermRemainingAmount(policy)).toFixed(2);
                return;
            case AMOUNT_OPTIONS.OTHER_AMOUNT:
                paymentRequest.paymentAmount = _.get(paymentRequest, 'selectedAmount.amount');
                break;
            default:
        }
    };

    if (!_.isEmpty(policiesBillingData)) {
        const initReinstateOptionAmount = (policy) => {
            selectedAmountOption.value = AMOUNT_OPTIONS.DUE_TO_REINSTATE;
            const paymentRequest = paymentRequests.value.find((request) => policy.policyDetails.policyNumber === request.policyNumber);
            if (paymentRequest) {
                setAmountToPay(paymentRequest, selectedAmountOption.value);
            }
        };

        policiesBillingData.forEach((policy) => {
            if (policy.selected) {
                totalAmountDue.amount += policy.totalOutstandingAmount_ML.amount + getEarlierTermOutstandingAmount(policy);
                totalPastDue.amount += policy.pastDue_ML.amount;
                totalAmountDueToReinstate.amount += policy.reinstatementAmountDue_ML.amount;
                totalInstallment.amount += policy.installmentAmount_ML.amount;
                totalBalance.amount += policy.remainingPolicyBalance_ML.amount + getEarlierTermRemainingAmount(policy);
                if (policy.pastDue_ML.amount > 0 && !policy.policyDetails.isCancelled) {
                    showNonPayStatusMessage = true;
                }
            }
            if (isReinstateOptionAvailable(policiesBillingData)) {
                initReinstateOptionAmount(policy);
            }
            // setSelectedPolicy(policy);
        });
    }

    // convert string to valid number
    const parsePaymentAmount = (value) => {
        const result = Number(value)

        if (Number.isNaN(result)) return 0

        return result
    }

    const setOtherPaymentAmount = (newVal, oldVal, policy) => {
        policy.selectedAmount = newVal;

        totalOtherAmount.amount += parsePaymentAmount(newVal.amount) - (oldVal ? parsePaymentAmount(oldVal.amount) : 0);
        if (parsePaymentAmount(newVal.amount) > 0) {
            selectedAmountOption.value = AMOUNT_OPTIONS.OTHER_AMOUNT;
        }
        if (selectedAmountOption.value === AMOUNT_OPTIONS.OTHER_AMOUNT) {
            setAmountToPay(policy, AMOUNT_OPTIONS.OTHER_AMOUNT);
        }
        onUpdatePaymentAmount();
    };

    const distributeAmount = (evt) => {
        selectedAmountOption.value = evt.target.id;
        paymentRequests.value.forEach((paymentRequest) => {
            setAmountToPay(paymentRequest, evt.target.id);
            // setSelectedPolicy(paymentRequest);
        });
        onUpdatePaymentAmount();
    };

    const isCancelledBetween7and46Days = (policyDetails) => {
        return policyDetails.isCancelled ?
            (Math.abs(MLLocalDateUtil.differenceInDays(MLLocalDateUtil.localDate2Date(policyDetails.cancelledDate_ML), new Date())) > 7
                && Math.abs(MLLocalDateUtil.differenceInDays(MLLocalDateUtil.localDate2Date(policyDetails.cancelledDate_ML), new Date())) < 46) : false;
    };

    const showCurrentAmountDueGhostOption = (policies) => {
        return policies && policies.some((policy) => policy.selected && policy.policyDetails.isCancelled && isNotReinstateState(policy) && policy.totalOutstandingAmount_ML.amount > 0
            && isCancelledBetween7and46Days(policy.policyDetails));
    };

    const isEntireBalanceOptionAvailable = (policies) => {
        return policies && policies.some((policy) => policy.selected);
    };

    const formatAmountString = (amount) => {
        const premium = _.get(amount, 'amount', 0);
        const currency = _.get(amount, 'currency', 'usd');

        try {
            return intl.formatNumber(premium, { style: 'currency', currency });
        } catch (e) {
            return '';
        }
    };

    const renderCurrentAmountDueHelpText = () => {
        const result = null
        const translatorOptions = { b: (chunks) => <b>{chunks}</b> }

        if (!isGpa) {
            return (
                <div id='currentAmountDueHelpText1' className="padding-left-0 margin-top-10" htmlFor="CurrentAmountDue">
                    <span className="display-block font-weight-normal vertical-spacing-10">{translator(messages.mustBePaidInFull, translatorOptions)}</span>
                    <span className="display-block font-weight-normal">{translator(messages.contactUsToReinstate)}</span>
                </div>)
        } if (showCurrentAmountDueGhostOption(policiesBillingData)) {
            return (
                <div id='currentAmountDueHelpText2' className="padding-left-0 margin-top-10" htmlFor="CurrentAmountDue">
                    <span className="display-block font-weight-normal">{translator(messages.mustBePaidInFull, translatorOptions)}</span>
                </div>
            )
        }

        return result
    }

    const getCurrentAmountDueContent = () => {
        return (
            <>
                <div className='cell' title={translator(messages.currentAmountDue)}>
                    <div className='ml-form-field-radio-group'>
                        {totalAmountDue.amount === 0 &&
                            <b>{translator(messages.currentAmountDue)}</b>
                        }
                        {totalAmountDue.amount > 0 &&
                            <>
                                <input
                                    type='radio'
                                    tabIndex='0'
                                    id={AMOUNT_OPTIONS.CURRENT_DUE}
                                    name="PaymentAmount"
                                    value={selectedAmountOption.value === AMOUNT_OPTIONS.CURRENT_DUE}
                                    onChange={(evt) => { distributeAmount(evt) }}
                                />
                                <label htmlFor={AMOUNT_OPTIONS.CURRENT_DUE}>
                                    {translator(messages.currentAmountDue)}
                                    {renderCurrentAmountDueHelpText()}
                                </label>
                            </>
                        }
                    </div>
                </div>
                <div className="cell">
                    <div className="clearfix">
                        <p className="body-copy font-weight-bold">{formatAmountString(totalAmountDue)}</p>
                    </div>
                </div>
            </>
        );
    };

    const getMultipleCurrentAmountDueContent = () => {
        return (
            <div className="cell column">
                {selectedAmountOption.value === AMOUNT_OPTIONS.CURRENT_DUE &&
                    _.filter(policiesBillingData, (policy) => policy.selected).map((policy) =>
                        <div className="clearfix vertical-spacing-20">
                            <div className="col-sm-9 text-align-left">
                                {policy.product_ML === 'Homeowners' && <p className="body-copy font-weight-bold">{translator(messages.homePolicy)}</p>}
                                {policy.product_ML === 'Personal Auto' && <p className="body-copy font-weight-bold">{translator(messages.autoPolicy)}</p>}
                                {policy.product_ML === 'Personal Umbrella' && <p className="body-copy font-weight-bold">{translator(messages.umbrellaPolicy)}</p>}
                                <p className="body-copy secondary light">
                                    {translator(messages.policyNumber, { policyNumber: policy.policyNumber_ML })}
                                </p>
                                <p className="body-copy secondary">
                                    {translator(messages.policyBalance)} {formatAmountString(policy.remainingPolicyBalance_ML)}
                                </p>
                            </div>
                            <div className="col-sm-3 text-align-right">
                                <p className="body-copy font-weight-bold">{formatAmountString(policy.totalOutstandingAmount_ML)}</p>
                            </div>
                        </div>
                    )}
            </div>
        );
    };

    const getAmountDueToReinstateContent = () => {
        return (
            <>
                <div className="cell" title={translator(messages.amountDueToReinstate)}>
                    <div className="ml-form-field-radio-group">
                        <input
                            type="radio"
                            tabIndex="0"
                            id={AMOUNT_OPTIONS.DUE_TO_REINSTATE}
                            name="PaymentAmount"
                            value={selectedAmountOption.value === AMOUNT_OPTIONS.DUE_TO_REINSTATE}
                            onChange={(evt) => { distributeAmount(evt) }}
                        />
                        <label className="ml-large-msg ml-bold-msg" htmlFor={AMOUNT_OPTIONS.DUE_TO_REINSTATE}>
                            {translator(messages.amountDueToReinstate)}
                        </label>
                    </div>
                </div>
                {paymentRequests.value.length === 1 &&
                    <div className="cell">
                        <p className="body-copy font-weight-bold">{formatAmountString(totalAmountDueToReinstate)}</p>
                    </div>
                }
                {paymentRequests.value.length > 1 &&
                    <div className="cell">
                        <p className="body-copy font-weight-bold">{formatAmountString(totalAmountDueToReinstate)}</p>
                    </div>
                }
            </>
        );
    };

    const getEntireBalanceContent = () => {
        return (
            <>
                <div className="cell" title={translator(messages.entireBalance)}>
                    <div className="ml-form-field-radio-group">
                        <input
                            type="radio"
                            tabIndex="0"
                            id={AMOUNT_OPTIONS.ENTIRE_BALANCE}
                            name="PaymentAmount"
                            value={selectedAmountOption.value === AMOUNT_OPTIONS.ENTIRE_BALANCE}
                            onChange={(evt) => { distributeAmount(evt) }}
                        />
                        <label className="ml-large-msg ml-bold-msg" htmlFor={AMOUNT_OPTIONS.ENTIRE_BALANCE}>
                            {translator(messages.entireBalance)}
                        </label>
                    </div>
                </div>
                <div className="cell">
                    <p className="body-copy font-weight-bold">{formatAmountString(totalBalance)}</p>
                </div>
            </>
        )
    };

    const getMultipleEntireBalanceContent = () => {
        return (
            <div className="cell column">
                {selectedAmountOption.value === AMOUNT_OPTIONS.ENTIRE_BALANCE &&
                    _.filter(policiesBillingData, (policy) => policy.selected).map((policy) =>
                        <div className="clearfix vertical-spacing-20">
                            <div className="col-sm-9 text-align-left">
                                {policy.product_ML === 'Homeowners' && <p className="body-copy font-weight-bold">{translator(messages.homePolicy)}</p>}
                                {policy.product_ML === 'Personal Auto' && <p className="body-copy font-weight-bold">{translator(messages.autoPolicy)}</p>}
                                {policy.product_ML === 'Personal Umbrella' && <p className="body-copy font-weight-bold">{translator(messages.umbrellaPolicy)}</p>}
                                <p className="body-copy secondary light">
                                    {translator(messages.policyNumber, { policyNumber: policy.policyNumber_ML })}
                                </p>
                                <p className="body-copy secondary">
                                    {translator(messages.policyBalance)} {formatAmountString(policy.remainingPolicyBalance_ML)}
                                </p>
                            </div>
                            <div className="col-sm-3 text-align-right">
                                <p className="body-copy font-weight-bold">{formatAmountString(policy.remainingPolicyBalance_ML)}</p>
                            </div>
                        </div>
                    )}
            </div>
        );
    };

    const getPaymentAmountValidationMessage = (selectedAmount, policyBalance) => {
        if (selectedAmount) {
            if (selectedAmount.amount === '' || !selectedAmount.amount) {
                return [translator(messages.requiredField)];
            }

            if (selectedAmount.amount < 1) {
                return [translator(messages.minAmount)];
            }

            if (policyBalance && selectedAmount.amount > policyBalance) {
                const balanceObj = {
                    amount: policyBalance,
                    currency: 'USD'
                }
                
                return [translator(messages.maxAmount, { max: formatAmountString(balanceObj) })];
            } else if (selectedAmount.amount > totalBalance.amount) {
                return [translator(messages.maxAmount, { max: formatAmountString(totalBalance) })];
            }
        }
        return undefined;
    };

    const getOtherAmountRowContent = () => {
        return (
            <>
                <div className="cell">
                    <div className="ml-form-field-radio-group">
                        <input
                            type="radio"
                            tabIndex="0"
                            id={AMOUNT_OPTIONS.OTHER_AMOUNT}
                            name="PaymentAmount"
                            value={selectedAmountOption.value === AMOUNT_OPTIONS.OTHER_AMOUNT}
                            checked={selectedAmountOption.value === AMOUNT_OPTIONS.OTHER_AMOUNT}
                            onChange={(evt) => { distributeAmount(evt) }}
                        />
                        <label htmlFor={AMOUNT_OPTIONS.OTHER_AMOUNT}>
                            {translator(messages.otherAmount)}
                        </label>
                    </div>
                </div>
                <div className="cell">
                    {paymentRequests.value.length === 1 &&
                        <CurrencyField
                            id={`${AMOUNT_OPTIONS.OTHER_AMOUNT}[0]_input`}
                            value={paymentRequests.value[0].selectedAmount}
                            validationMessages={getPaymentAmountValidationMessage(paymentRequests.value[0].selectedAmount)}
                            onValueChange={(value) => { setOtherPaymentAmount(value, paymentRequests.value[0].selectedAmount, paymentRequests.value[0]) }}
                            placeholder={translator(messages.enterAmount)}
                            showErrors
                            required={selectedAmountOption.value === AMOUNT_OPTIONS.OTHER_AMOUNT}
                        />
                    }
                    {paymentRequests.value.length > 1 &&
                        <div className="clearfix">
                            <p className="body-copy font-weight-bold">{formatAmountString(totalOtherAmount)}</p>
                        </div>
                    }
                </div>
            </>
        );
    };

    const getMultipleOtherAmountDueContent = () => {
        return (
            <div className="cell column">
                {paymentRequests.children.map((paymentRequest, index) =>
                    <div className="clearfix vertical-spacing-20">
                        <div className="col-sm-9 text-align-left">
                            {isHomePolicy(paymentRequest.value.productCode) && <p className="body-copy font-weight-bold">{translator(messages.homePolicy)}</p>}
                            {isAutoPolicy(paymentRequest.value.productCode) && <p className="body-copy font-weight-bold">{translator(messages.autoPolicy)}</p>}
                            {isUmbrellaPolicy(paymentRequest.value.productCode) && <p className="body-copy font-weight-bold">{translator(messages.umbrellaPolicy)}</p>}
                            <p className="body-copy secondary light">
                                {translator(messages.policyNumber, { policyNumber: paymentRequest.policyNumber.value })}
                            </p>
                            <p className={styles['policy-balance-row']}>
                                {translator(messages.policyBalance)}
                                {/* paymentRequest does not contain currency property */}
                                <CurrencyValue
                                    currencyDisplay='symbol'
                                    value={{ amount: paymentRequest.policyBalance.value }}
                                    showFractions
                                    className={styles['policy-balance-value']}
                                />
                            </p>
                        </div>
                        <div gw-pl-ctrl-group model="paymentRequest.paymentAmount" className="col-sm-3">
                            <CurrencyField
                                id={`${AMOUNT_OPTIONS.OTHER_AMOUNT}[${index}]_input`}
                                value={paymentRequest.value.selectedAmount}
                                validationMessages={getPaymentAmountValidationMessage(paymentRequest.value.selectedAmount, paymentRequest.value.policyBalance)}
                                onValueChange
                                ={(value) => { setOtherPaymentAmount(value, paymentRequest.value.selectedAmount, paymentRequest.value) }}
                                placeholder={translator(messages.enterAmount)}
                                showErrors
                                required
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const overrides = {
        noPayStatusMessage: {
            visible: showNonPayStatusMessage,
            content: translator(messages.nonPayStatusMessage)
        },
        paymentTable: {
            className: isGpa ? 'stack-table plain-reverse' : 'stack-table plain-reverse payments-spacing'
        },
        onetimePaymentMessage: {
            content: translator(messages.onetimePaymentMessage)
        },
        currentAmountDueRow: {
            content: getCurrentAmountDueContent()
        },
        multipleCurrentAmountRow: {
            visible: paymentRequests.value.length > 1 && selectedAmountOption.value === AMOUNT_OPTIONS.CURRENT_DUE,
            content: getMultipleCurrentAmountDueContent()
        },
        amountDueToReinstateRow: {
            visible: isReinstateOptionAvailable(policiesBillingData),
            content: getAmountDueToReinstateContent()
        },
        entireBalanceRow: {
            visible: isEntireBalanceOptionAvailable(policiesBillingData),
            content: getEntireBalanceContent()
        },
        multipleEntireBalanceRow: {
            visible: paymentRequests.value.length > 1 && selectedAmountOption.value === AMOUNT_OPTIONS.ENTIRE_BALANCE,
            content: getMultipleEntireBalanceContent()
        },
        otherAmountRow: {
            content: getOtherAmountRowContent()
        },
        multipleOtherAmountRow: {
            visible: paymentRequests.value.length > 1 && selectedAmountOption.value === AMOUNT_OPTIONS.OTHER_AMOUNT,
            content: getMultipleOtherAmountDueContent()
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default MLSelectPaymentAmountComponent;